/**
 * @fileoverview Drawer that lists bookmarked notes using `useBookmarkedNotes`.
 * Uses DrawerContentWrapper for consistent loading/error/empty state handling.
 */

import { AccessTime, Favorite } from '@mui/icons-material';
import { Box, ButtonBase, Divider, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { useBookmarkedNotes, type BookmarkedNote } from '../hooks/useBookmarkedNotes';
import { formatDate, truncateContent } from '../utils/noteUtils';
import type { BookmarksDrawerProps } from '../types';
import { DRAWER_WIDTH } from '@/config/constants';
import { DrawerContainer } from '@/shared/components';
import { DrawerContentWrapper } from './DrawerContentWrapper';

/** Shared list item styles for drawer items */
const drawerListItemSx = {
  flexDirection: 'column',
  alignItems: 'flex-start',
  '&:hover': { backgroundColor: 'action.hover' },
  borderRadius: 1,
  p: 2,
} as const;

export function BookmarksDrawer({
  open,
  onClose,
  onNoteClick,
}: BookmarksDrawerProps): React.JSX.Element {
  const { bookmarkedNotes, loading, error } = useBookmarkedNotes();

  function handleNoteClick(note: BookmarkedNote): void {
    if (onNoteClick) {
      onNoteClick(note.id);
      return;
    }
    window.location.assign(`/${note.id}`);
  }

  return (
    <DrawerContainer
      anchor="right"
      open={open}
      onClose={onClose}
      width={DRAWER_WIDTH.BOOKMARKS}
    >
      <DrawerContentWrapper<BookmarkedNote>
        loading={loading}
        error={error}
        data={bookmarkedNotes}
        emptyState={{
          icon: <Favorite />,
          title: 'No bookmarked notes yet',
          subtitle: 'Bookmark your favorite notes to see them here',
        }}
        renderContent={(notes) => (
          <List sx={{ p: 0 }}>
            {notes.map((note, index) => (
              <div key={note.id}>
                <ButtonBase
                  onClick={() => handleNoteClick(note)}
                  sx={{
                    width: '100%',
                    display: 'block',
                    textAlign: 'left',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItem sx={drawerListItemSx}>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "medium",
                            mb: 1
                          }}>
                          {note.title || 'Untitled Note'}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              mb: 1,
                              lineHeight: 1.4
                            }}>
                            {truncateContent(note.content, 100)}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{
                            alignItems: "center"
                          }}>
                            <AccessTime sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" sx={{
                              color: "text.secondary"
                            }}>
                              {formatDate(note.updated_at)}
                            </Typography>
                          </Stack>
                        </Box>
                      }
                    />
                  </ListItem>
                </ButtonBase>
                {index < notes.length - 1 && <Divider />}
              </div>
            ))}
          </List>
        )}
      />
    </DrawerContainer>
  );
}